import { ChevronRight, MessageSquare, type LucideIcon } from "lucide-react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { timeAgo } from "@/shared/lib/timeAgo";
import type { CompanionListAgent } from "../lib/chatAgentRegistry";

export interface ChatListRowAction {
  icon: LucideIcon;
  onClick: () => void;
  disabled?: boolean;
  title?: string;
  tone?: "primary" | "danger";
}

export interface ChatListRowProps {
  agent: CompanionListAgent;
  lastMessage?: { text: string; timestamp: string } | null;
  unreadCount: number;
  jobCount: number;
  isStreaming?: boolean;
  onClick: () => void;
  /** Number of sessions available for this agent (undefined hides the pill). */
  sessionCount?: number;
  /** ISO timestamp of the most recent session (undefined falls back to lastMessage.timestamp). */
  lastUsedAt?: string | null;
  /** Optional trailing action — replaces the chevron when provided. */
  action?: ChatListRowAction;
  dataHighlightId?: string;
  showcaseState?: "visible" | "featured" | "muted";
  /**
   * Bumps to a new positive integer whenever Marcus's onboarding triggers the
   * piano-key team-sweep animation. Combined with `sweepDelayMs` to play a
   * staggered activation across the list. `0` (default) means no sweep.
   */
  sweepGeneration?: number;
  /**
   * Delay before this row "lights up" during the team-sweep, measured from
   * the moment the sweep generation changes.
   */
  sweepDelayMs?: number;
}

function ChatListAgentAvatar({ agent }: { agent: CompanionListAgent }) {
  const Icon = agent.icon;

  if ("imageSrc" in agent) {
    return (
      <img
        src={agent.imageSrc}
        alt={agent.name}
        className="h-[40px] w-[40px] rounded-full object-cover"
      />
    );
  }

  if ("subAgentId" in agent && agent.listAvatar) {
    const Avatar = agent.listAvatar;
    return <Avatar className="h-[40px] w-[40px] rounded-full" />;
  }

  return (
    <div className="flex h-[40px] w-[40px] items-center justify-center rounded-full bg-background/35 ring-1 ring-border/30">
      <Icon className={cn("h-5 w-5", agent.color)} />
    </div>
  );
}

export function ChatListRow({
  agent,
  lastMessage,
  unreadCount,
  jobCount,
  isStreaming,
  onClick,
  sessionCount,
  lastUsedAt,
  action,
  dataHighlightId,
  showcaseState,
  sweepGeneration = 0,
  sweepDelayMs = 0,
}: ChatListRowProps) {
  const ActionIcon = action?.icon;
  const lastUsedLabel = lastUsedAt ? timeAgo(lastUsedAt) : lastMessage?.timestamp ?? null;
  const showSessions = typeof sessionCount === "number";
  const isShowcase = showcaseState !== undefined;
  const isFeatured = showcaseState === "featured";
  const isMuted = showcaseState === "muted";
  const avatarAccent = "chatBgHex" in agent ? agent.chatBgHex : agent.bgHex;
  const sweepActive = sweepGeneration > 0;

  return (
    <motion.div
      data-highlight-id={dataHighlightId}
      layout
      initial={{ opacity: 0, y: 6 }}
      animate={{
        opacity: isMuted ? 0.68 : 1,
        scale: isFeatured ? 1.012 : 1,
        x: isFeatured ? -2 : 0,
      }}
      transition={{ type: "spring", stiffness: 420, damping: 34, mass: 0.7 }}
      className={cn(
        "relative w-full flex items-center border-y border-transparent hover:bg-muted/30 transition-colors",
        isShowcase && "mx-2 my-1 w-[calc(100%-1rem)] rounded-lg",
        isFeatured && "border-primary/25 bg-card/85 shadow-ambient",
        unreadCount > 0 && "bg-primary/[0.03]",
      )}
    >
      {sweepActive && (
        <motion.span
          key={`sweep-${sweepGeneration}`}
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-lg"
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: [0, 1, 0.6, 0], scale: [0.96, 1.04, 1.015, 1] }}
          transition={{
            duration: 0.85,
            delay: sweepDelayMs / 1000,
            ease: "easeOut",
            times: [0, 0.25, 0.55, 1],
          }}
          style={{
            boxShadow:
              "0 0 0 1px hsl(var(--primary) / 0.55), 0 0 32px 4px hsl(var(--primary) / 0.4)",
            background:
              "linear-gradient(90deg, hsl(var(--primary) / 0.10) 0%, hsl(var(--primary) / 0.22) 50%, hsl(var(--primary) / 0.08) 100%)",
          }}
        />
      )}
      {isFeatured && (
        <motion.span
          className="absolute left-1 top-2 bottom-2 w-1 rounded-full bg-primary/70"
          initial={{ opacity: 0, scaleY: 0.55 }}
          animate={{ opacity: 1, scaleY: 1 }}
          transition={{ type: "spring", stiffness: 500, damping: 36 }}
          style={{ transformOrigin: "center" }}
          aria-hidden
        />
      )}
      <motion.button
        type="button"
        onClick={onClick}
        whileHover={{ x: isShowcase ? 2 : 0 }}
        whileTap={{ scale: 0.99 }}
        className={cn(
          "flex-1 min-w-0 flex items-center gap-3 py-3 text-left",
          isShowcase ? "pl-4 pr-3" : "px-4",
        )}
      >
        {/* Agent icon */}
        <div className="relative flex h-[52px] w-[52px] shrink-0 items-center justify-center">
          <div
            className={cn(
              "relative flex h-[52px] w-[52px] items-center justify-center rounded-2xl",
              jobCount > 0 && "ring-2 ring-primary/30",
            )}
            style={{ backgroundColor: `${avatarAccent}20` }}
          >
            <ChatListAgentAvatar agent={agent} />
          </div>
          {jobCount > 0 && (
            <span className="absolute -right-1 -top-1 z-20 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold tabular-nums text-white shadow-sm">
              {jobCount}
            </span>
          )}
        </div>

        {/* Name + preview */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-0.5">
            <span
              className={cn(
                "text-sm font-medium truncate",
                unreadCount > 0 ? "text-foreground font-semibold" : "text-foreground/90",
              )}
            >
              {agent.name}
            </span>
            {lastUsedLabel && (
              <span className="text-[10px] text-muted-foreground/60 shrink-0 tabular-nums">
                {lastUsedLabel}
              </span>
            )}
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs text-muted-foreground truncate">
              {isStreaming
                ? "Typing..."
                : lastMessage?.text || agent.role}
            </span>
            <div className="shrink-0 flex items-center gap-1.5">
              {showSessions && sessionCount! > 0 && (
                <span
                  className="flex items-center gap-0.5 text-[10px] text-muted-foreground/70 tabular-nums"
                  title={`${sessionCount} session${sessionCount === 1 ? "" : "s"}`}
                >
                  <MessageSquare className="w-3 h-3" />
                  {sessionCount}
                </span>
              )}
              {unreadCount > 0 && (
                <span className="flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-[10px] text-white font-bold tabular-nums">
                  {unreadCount}
                </span>
              )}
            </div>
          </div>
        </div>
      </motion.button>

      {/* Trailing action or chevron */}
      <div className="pr-3 shrink-0 flex items-center">
        {action && ActionIcon ? (
          <button
            type="button"
            onClick={action.onClick}
            disabled={action.disabled}
            title={action.title}
            className={cn(
              "flex items-center justify-center w-8 h-8 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed",
              action.tone === "danger"
                ? "bg-destructive/10 text-destructive hover:bg-destructive hover:text-destructive-foreground"
                : "bg-primary/10 hover:bg-primary hover:text-white text-primary",
            )}
          >
            <ActionIcon className="w-4 h-4" />
          </button>
        ) : (
          <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/40" />
        )}
      </div>
    </motion.div>
  );
}
